import { useAppTranslation } from "@/utils";
import { ExclamationCircleOutlined } from "@payping/icons";
import { Button, Flex, Form, Modal, Select, TextArea, Typography, useMessagedAsyncFn } from "@payping/web-components";
import { FC, useState } from "react";
import { bnplMerchantApis } from "@payping/api";

interface CancelOrderProps {
  open: boolean;
  onCancel: () => void;
  trackingCode: string;
  onFinish: () => void;
}
interface FormValues {
  reason: number;
  description: string;
}
const BnplListDetailsPageCancelOrderChequeModal: FC<CancelOrderProps> = ({
  onCancel,
  open,
  trackingCode,
  onFinish,
}) => {
  const { t } = useAppTranslation();
  const [acceptRules, setAccessRules] = useState(false);

  const [form] = Form.useForm<FormValues>();

  const reasonsList = t("shop.bnpl.orderDetails.waitingForChequeConfirm.reject.form.reason.options", {
    returnObjects: true,
  }) as string[];

  const [cancelOrderState, cancelOrder] = useMessagedAsyncFn(
    async (reason: string, description: string) => {
      await bnplMerchantApis.orderCancelMerchant({ trackingCode, description, reason });
      onCancel();
      onFinish();
    },
    [trackingCode, onFinish],
    { loading: false },
    { success: t("shop.bnpl.orderDetails.successCancelOrder") },
  );

  const hnadleSubmitCancelOrder = async (data: FormValues) => {
    await cancelOrder(reasonsList[data.reason], data.description ?? "");
    onCancel();
    onFinish();
  };

  if (!open) {
    return null;
  }

  if (acceptRules)
    return (
      <Modal
        open={open}
        onCancel={onCancel}
        title={
          <Flex align="center" gap={"middle"}>
            {t("shop.bnpl.orderDetails.waitingForChequeConfirm.reject.warningModal.title")}
          </Flex>
        }
        footer={
          <Flex align="center" justify="end" gap={"middle"}>
            <Button type="default" onClick={() => onCancel()} disabled={cancelOrderState.loading}>
              {t("common.cancel")}
            </Button>
            <Button variant="solid" color="danger" onClick={() => form.submit()} loading={cancelOrderState.loading}>
              {t("shop.bnpl.orderDetails.waitingForChequeConfirm.reject.form.submit")}
            </Button>
          </Flex>
        }
      >
        <Flex vertical>
          <Form layout="vertical" form={form} onFinish={hnadleSubmitCancelOrder}>
            <Form.Item<FormValues>
              rules={[{ required: true }]}
              required
              label={t("shop.bnpl.orderDetails.waitingForChequeConfirm.reject.form.reason.label")}
              name={"reason"}
            >
              <Select options={reasonsList.map((item, index) => ({ value: index, label: item }))} />
            </Form.Item>
            <Form.Item<FormValues>
              rules={[{ max: 180 }]}
              name="description"
              label={t("shop.bnpl.orderDetails.waitingForChequeConfirm.reject.form.description.label")}
            >
              <TextArea maxLength={180} numberOfLines={3} />
            </Form.Item>
          </Form>
          <Typography.Text>{t("shop.bnpl.orderDetails.waitingForChequeConfirm.reject.form.content")}</Typography.Text>
        </Flex>
      </Modal>
    );

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      closeIcon={null}
      title={
        <Flex align="center" gap={"middle"}>
          <ExclamationCircleOutlined className="tw-text-color-warning" width={20} />
          {t("shop.bnpl.orderDetails.waitingForChequeConfirm.reject.warningModal.title")}
        </Flex>
      }
      footer={
        <Flex align="center" justify="end" gap={"middle"}>
          <Button type="default" onClick={() => onCancel()}>
            {t("common.cancel")}
          </Button>
          <Button variant="solid" color="danger" onClick={() => setAccessRules(true)}>
            {t("shop.bnpl.orderDetails.waitingForChequeConfirm.reject.warningModal.submit")}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={"middle"} className="tw-pr-9">
        <Typography.Text>
          {t("shop.bnpl.orderDetails.waitingForChequeConfirm.reject.warningModal.content")}
        </Typography.Text>
      </Flex>
    </Modal>
  );
};

export default BnplListDetailsPageCancelOrderChequeModal;
